import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { getCurrentLanguage } from '../constants/localization';
import { useSettingsRedux } from '../hooks/useSettingsRedux';
import { useSession } from '../context/SessionContext';
import { ApiManager } from '../services/ApiManager';
import './Rules.css';

// Supported languages for rules
const SUPPORTED_LANGUAGES = ['fi', 'de', 'en'] as const;
type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

// Default fallback language
const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

const Rules: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { settings } = useSettingsRedux();
  const { sessionId } = useSession();
  const [markdownContent, setMarkdownContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // State for managing fade effects
  const [isVisible, setIsVisible] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const rulesPageRef = useRef<HTMLDivElement>(null);

  // Helper function to check if a language is supported
  const isSupportedLanguage = (lang: string): lang is SupportedLanguage => {
    return SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage);
  };

  // Helper function to load rules for a specific language
  const loadRulesForLanguage = async (language: SupportedLanguage): Promise<string> => {
    try {
      const response = await fetch(`/rules-${language}.md`);
      if (!response.ok) {
        throw new Error(`Failed to load rules for ${language}: ${response.status} ${response.statusText}`);
      }
      const text = await response.text();
      return text;
    } catch (error) {
      console.error(`Error loading rules for ${language}:`, error);
      throw error;
    }
  };

  // Load markdown content with fallback logic
  useEffect(() => {
    const loadRules = async () => {
      setLoading(true);
      setError(null);

      try {
        // Get current language from localization config
        const configLanguage = getCurrentLanguage().toLowerCase();
        let targetLanguage: SupportedLanguage;

        // Check if the current language is supported
        if (isSupportedLanguage(configLanguage)) {
          targetLanguage = configLanguage;
        } else {
          console.warn(`Language "${configLanguage}" not supported for rules, falling back to ${DEFAULT_LANGUAGE}`);
          targetLanguage = DEFAULT_LANGUAGE;
        }

        try {
          // Try to load the target language
          const content = await loadRulesForLanguage(targetLanguage);
          setMarkdownContent(content);
        } catch (error) {
          // If target language fails and it's not the default, try default
          if (targetLanguage !== DEFAULT_LANGUAGE) {
            console.warn(`Failed to load rules for ${targetLanguage}, trying ${DEFAULT_LANGUAGE}`);
            const fallbackContent = await loadRulesForLanguage(DEFAULT_LANGUAGE);
            setMarkdownContent(fallbackContent);
          } else {
            throw error; // If default also fails, let the error bubble up
          }
        }
      } catch (error) {
        console.error('Failed to load rules content:', error);
        setError('Unable to load rules content. Please try again later.');
        setMarkdownContent('# Error\n\nUnable to load rules content. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadRules();
  }, []); // Empty dependency array since we want this to run only once on mount

  // Track when component is first mounted
  useEffect(() => {
    setIsInitialized(true);
    // Trigger fade-in when component mounts
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 50); // Small delay to ensure the component is rendered

    return () => clearTimeout(timer);
  }, []);

  // Handle fade effects when location changes
  useEffect(() => {
    if (!isInitialized) return;

    const rootPath = process.env.REACT_APP_ROOT_PATH || '';
    const isRulesRoute = location.pathname.includes('/rules');

    if (isRulesRoute) {
      // Fade in when returning to Rules page
      setIsVisible(false); // Start from invisible
      const timer = setTimeout(() => {
        setIsVisible(true); // Then fade in
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [location.pathname, isInitialized]);

  // Apply opacity to rules page based on visibility state
  useEffect(() => {
    if (rulesPageRef.current) {
      rulesPageRef.current.style.opacity = isVisible ? '1' : '0';
    }
  }, [isVisible]);

  // Reset visibility when component unmounts to avoid stale state
  useEffect(() => {
    return () => {
      setIsVisible(false);
    };
  }, []);

  // Send analytics event when rules page is viewed
  useEffect(() => {
    const sendRulesAnalytics = async () => {
      if (sessionId) {
        try {
          await ApiManager.analytics.playmerce_rules(sessionId);
        } catch (error) {
          console.error('Failed to send rules analytics:', error);
        }
      }
    };

    sendRulesAnalytics();
  }, [sessionId]); // Only re-run if sessionId changes

  const handleGoBack = () => {
    if (isTransitioning) return; // Prevent multiple rapid clicks

    const rootPath = process.env.REACT_APP_ROOT_PATH || '';
    // Trigger fade out before navigation
    setIsVisible(false);
    setIsTransitioning(true);
    // Small delay to allow fade effect to show before navigation
    setTimeout(() => {
      // Dispatch a custom popstate event to trigger the back animation
      window.dispatchEvent(new PopStateEvent('popstate'));
      // Navigate back to the game path
      navigate(`${rootPath}/game`);
      setIsTransitioning(false);
    }, 150);
  };

  // Retry loading the current language
  const handleRetry = () => {
    // Re-run the loading effect
    const loadRules = async () => {
      setLoading(true);
      setError(null);

      try {
        const configLanguage = getCurrentLanguage().toLowerCase();
        let targetLanguage: SupportedLanguage;

        if (isSupportedLanguage(configLanguage)) {
          targetLanguage = configLanguage;
        } else {
          console.warn(`Language "${configLanguage}" not supported for rules, falling back to ${DEFAULT_LANGUAGE}`);
          targetLanguage = DEFAULT_LANGUAGE;
        }

        try {
          const content = await loadRulesForLanguage(targetLanguage);
          setMarkdownContent(content);
        } catch (error) {
          if (targetLanguage !== DEFAULT_LANGUAGE) {
            console.warn(`Failed to load rules for ${targetLanguage}, trying ${DEFAULT_LANGUAGE}`);
            const fallbackContent = await loadRulesForLanguage(DEFAULT_LANGUAGE);
            setMarkdownContent(fallbackContent);
          } else {
            throw error;
          }
        }
      } catch (error) {
        console.error('Failed to load rules content:', error);
        setError('Unable to load rules content. Please try again later.');
        setMarkdownContent('# Error\n\nUnable to load rules content. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadRules();
  };

  return (
    <div className="rules-page" ref={rulesPageRef}>
      <button className="back-button" onClick={handleGoBack}>
        <span className="back-button-icon">←</span> {settings?.translations?.product?.back ?? 'Back'}
      </button>

      <div className="rules-content">
        {loading && (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading rules...</p>
          </div>
        )}

        {error && (
          <div className="error-state">
            <h3>Error Loading Rules</h3>
            <p>{error}</p>
            <button onClick={handleRetry} className="retry-button" disabled={loading}>
              Retry
            </button>
          </div>
        )}

        {!loading && !error && <ReactMarkdown>{markdownContent}</ReactMarkdown>}
      </div>
    </div>
  );
};

export default Rules;
